import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { fetchUnreadCount, fetchNotifications, markNotificationRead, markAllNotificationsRead } from './api';
import { useSnackbar } from './SnackbarContext';

const NotificationContext = createContext();

export function useNotifications() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const { token } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevUnreadCount = useRef(0);

  const refreshUnreadCount = useCallback(async () => {
    if (!token) {
      setUnreadCount(0);
      return;
    }
    try {
      const data = await fetchUnreadCount(token);
      const newCount = data.unread_count || 0;
      // Show toast if we have new notifications
      if (newCount > prevUnreadCount.current && prevUnreadCount.current > 0) {
        showSnackbar('You have new notifications!', 'info');
      }
      prevUnreadCount.current = newCount;
      setUnreadCount(newCount);
    } catch {
      // Ignore errors
    }
  }, [token, showSnackbar]);

  const refreshNotifications = useCallback(async () => {
    if (!token) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchNotifications(token, false, 50);
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      // Ignore errors
    } finally {
      setLoading(false);
    }
  }, [token]);

  const markRead = useCallback(async (notificationId) => {
    if (!token) return;
    try {
      await markNotificationRead(token, notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  }, [token]);

  const markAllRead = useCallback(async () => {
    if (!token) return;
    try {
      await markAllNotificationsRead(token);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  }, [token]);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  const value = {
    unreadCount,
    notifications,
    loading,
    refreshUnreadCount,
    refreshNotifications,
    markRead,
    markAllRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
